import React, { useId } from "react";

const DEFAULT_SIZE = 64;

/**
 * Flipmail loader: scanning envelope with subtle flap motion and delayed fade-in.
 */
export function FlipmailMailboxLoader({
  size = DEFAULT_SIZE,
  className = "",
  text = null,
  decorative = false,
  vivid = false,
}) {
  const reactId = useId().replace(/:/g, "");
  const maskId = `flipmail-env-mask-${reactId}`;

  const parsed = Number(size);
  const iconSize = Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_SIZE;
  const bodyFill = vivid ? 0.12 : 0.07;
  const flapFill = vivid ? 0.1 : 0.055;
  const strokeMain = vivid ? 0.5 : 0.38;
  const strokeSoft = vivid ? 0.32 : 0.22;
  const scanOpacity = vivid ? 0.55 : 0.4;
  const linesOpacity = vivid ? 0.35 : 0.22;

  return (
    <>
      <style>{`
        @keyframes flipmailLoaderFadeIn {
          from {
            opacity: 0;
            transform: scale(0.94) translateY(6px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
      <span
        role={decorative ? undefined : "status"}
        aria-live={decorative ? undefined : "polite"}
        aria-label={decorative ? undefined : text || "Loading"}
        aria-hidden={decorative ? true : undefined}
        className={`inline-flex flex-col items-center justify-center text-center text-theme-accent ${className}`.trim()}
        style={{
          animation: "flipmailLoaderFadeIn 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.2s both",
        }}
      >
        <svg
          width={iconSize}
          height={iconSize}
          viewBox="0 0 100 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <defs>
            <mask id={maskId} maskUnits="userSpaceOnUse">
              <path d="M 22 36 L 78 36 L 78 74 L 22 74 Z" fill="white" />
            </mask>
          </defs>

          <g strokeLinecap="round" strokeLinejoin="round">
            <path
              d="M 22 36 L 78 36 L 78 74 L 22 74 Z"
              fill="currentColor"
              fillOpacity={bodyFill}
              stroke="currentColor"
              strokeWidth="1.65"
              strokeOpacity={strokeMain}
            />

            <g stroke="currentColor" strokeWidth="1.1" strokeOpacity={linesOpacity}>
              <path d="M 30 48 H 70">
                <animate
                  attributeName="stroke-opacity"
                  values={`${linesOpacity * 0.45};${linesOpacity};${linesOpacity * 0.45}`}
                  dur="2s"
                  repeatCount="indefinite"
                />
              </path>
              <path d="M 30 56 H 62">
                <animate
                  attributeName="stroke-opacity"
                  values={`${linesOpacity * 0.55};${linesOpacity * 1.05};${linesOpacity * 0.55}`}
                  dur="2.4s"
                  begin="0.25s"
                  repeatCount="indefinite"
                />
              </path>
              <path d="M 30 64 H 58">
                <animate
                  attributeName="stroke-opacity"
                  values={`${linesOpacity * 0.5};${linesOpacity * 1.1};${linesOpacity * 0.5}`}
                  dur="2.1s"
                  begin="0.5s"
                  repeatCount="indefinite"
                />
              </path>
            </g>

            <g mask={`url(#${maskId})`}>
              <line
                x1="26"
                y1="40"
                x2="74"
                y2="40"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeOpacity={scanOpacity}
              >
                <animate attributeName="y1" values="38;72;38" dur="2.35s" repeatCount="indefinite" />
                <animate attributeName="y2" values="38;72;38" dur="2.35s" repeatCount="indefinite" />
              </line>
            </g>

            <g>
              <animateTransform
                attributeName="transform"
                type="rotate"
                values="-3.5 50 36; 2 50 36; -3.5 50 36"
                dur="3.2s"
                repeatCount="indefinite"
              />
              <path
                d="M 22 36 L 50 56 L 78 36"
                fill="currentColor"
                fillOpacity={flapFill}
                stroke="currentColor"
                strokeWidth="1.65"
                strokeOpacity={strokeMain}
              />
            </g>

            <circle cx="50" cy="50" r="3.4" fill="currentColor" fillOpacity={vivid ? 0.42 : 0.3}>
              <animate
                attributeName="fill-opacity"
                values={`${vivid ? 0.25 : 0.18};${vivid ? 0.55 : 0.42};${vivid ? 0.25 : 0.18}`}
                dur="2.1s"
                repeatCount="indefinite"
              />
            </circle>

            <path
              d="M 16 78 Q 50 82 84 78"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeOpacity={strokeSoft}
              fill="none"
            />
          </g>
        </svg>

        {text ? (
          <span className="mt-3 max-w-[16rem] font-mono text-[11px] uppercase tracking-[0.1em] text-theme-dim">
            {text}
          </span>
        ) : null}
      </span>
    </>
  );
}
